import React from 'react';

import { type EmailMessagesFilterInput, useEmailMessagesReportQuery } from '@dashboard/generated/wp/platform';
import { Skeleton } from '@mui/material';

import { NumberFormatter } from '@waypoint/react/formatters';

import ReportBarMetricMarkup, { ReportBarMetricError } from './ReportBarMetricMarkup';

const EVENT_TYPE_VALUES = [
  'ALL',
  'DELIVERED',
  'OPENED',
  'CLICKED',
  'BOUNCED',
  'COMPLAINED',
  'SUPPRESSED',
  'ERRORED',
  'UNSUBSCRIBED',
] as const;
type TMetricEventType = (typeof EVENT_TYPE_VALUES)[number];

type Props = {
  template: TMetricEventType;
  filter: EmailMessagesFilterInput;
};

type PercentageProps = {
  title: string;
  num: number;
  den: number;
};
function PercentageMetric({ title, num, den }: PercentageProps) {
  const percentage = den === 0 ? 0 : (num / den) * 100;
  return (
    <ReportBarMetricMarkup
      title={title}
      primaryValue={<NumberFormatter value={percentage} maximumSignificantDigits={2} percentage />}
      secondaryValue={<NumberFormatter value={num} />}
    />
  );
}

export default function ReportBarMetric({ template, filter }: Props) {
  const { loading, data } = useEmailMessagesReportQuery({ variables: { filter } });

  if (loading) {
    switch (template) {
      case 'ALL':
        return <ReportBarMetricMarkup title="Created" primaryValue={<Skeleton width={48} />} />;
      case 'DELIVERED':
        return <ReportBarMetricMarkup title="Delivered" primaryValue={<Skeleton width={48} />} />;
      case 'OPENED':
        return <ReportBarMetricMarkup title="Opened" primaryValue={<Skeleton width={48} />} />;
      case 'CLICKED':
        return <ReportBarMetricMarkup title="Clicked" primaryValue={<Skeleton width={48} />} />;
      case 'BOUNCED':
        return <ReportBarMetricMarkup title="Bounced" primaryValue={<Skeleton width={48} />} />;
      case 'COMPLAINED':
        return <ReportBarMetricMarkup title="Complained" primaryValue={<Skeleton width={48} />} />;
      case 'ERRORED':
        return <ReportBarMetricMarkup title="Errored" primaryValue={<Skeleton width={48} />} />;
      case 'SUPPRESSED':
        return <ReportBarMetricMarkup title="Suppressed" primaryValue={<Skeleton width={48} />} />;
      case 'UNSUBSCRIBED':
        return <ReportBarMetricMarkup title="Unsubscribed" primaryValue={<Skeleton width={48} />} />;
    }
  }

  if (data === undefined) {
    switch (template) {
      case 'ALL':
        return <ReportBarMetricError title="Created" />;
      case 'DELIVERED':
        return <ReportBarMetricError title="Delivered" />;
      case 'OPENED':
        return <ReportBarMetricError title="Opened" />;
      case 'CLICKED':
        return <ReportBarMetricError title="Clicked" />;
      case 'BOUNCED':
        return <ReportBarMetricError title="Bounced" />;
      case 'COMPLAINED':
        return <ReportBarMetricError title="Complained" />;
      case 'ERRORED':
        return <ReportBarMetricError title="Errored" />;
      case 'SUPPRESSED':
        return <ReportBarMetricError title="Suppressed" />;
      case 'UNSUBSCRIBED':
        return <ReportBarMetricError title="Unsubscribed" />;
    }
  }

  const { report, count } = data.EmailMessages;

  switch (template) {
    case 'ALL':
      return <ReportBarMetricMarkup title="Created" primaryValue={<NumberFormatter value={count} />} />;
    case 'DELIVERED':
      return <PercentageMetric title="Delivered" num={report.deliveredCount} den={count} />;
    case 'OPENED':
      return <PercentageMetric title="Opened" num={report.openedCount} den={count} />;
    case 'CLICKED':
      return <PercentageMetric title="Clicked" num={report.clickedCount} den={count} />;
    case 'COMPLAINED':
      return <PercentageMetric title="Complained" num={report.complainedCount} den={count} />;
    case 'BOUNCED':
      return <PercentageMetric title="Bounced" num={report.bouncedCount} den={count} />;
    case 'ERRORED':
      return <PercentageMetric title="Errored" num={report.erroredCount} den={count} />;
    case 'SUPPRESSED':
      return <PercentageMetric title="Suppressed" num={report.suppressedCount} den={count} />;
    case 'UNSUBSCRIBED':
      return <PercentageMetric title="Unsubscribed" num={report.unsubscribedCount} den={count} />;
  }
}
